import { memo, useCallback, useState, useEffect } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import axios from 'axios';
import PropTypes from 'prop-types';
import { generatePath } from 'react-router-dom';

import CheckDepositHistory from '#client/components/CheckDepositHistory';
import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import Switch from '#client/components/Switch';
import ViewAllTransactionLimitButton from '#client/components/ViewAllTransactionLimitButton';
import { MODAL_NAMES } from '#client/constants/modals';
import { FUND_BANK_ACCOUNT } from '#client/constants/nextUpCards';
import { TOKEN_TYPES } from '#client/constants/tokens';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useCheckDepositLimits from '#client/hooks/useCheckDepositLimits';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useCheckTransactionContext from '#client/hooks/useCheckTransactionContext';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useFormStep from '#client/hooks/useFormStep';
import useMitekSdkContext from '#client/hooks/useMitekSdk';
import { CHECK_FRONT_DOC_TYPE } from '#client/hooks/useMitekSdk/constants';
import useModalContext from '#client/hooks/useModalContext';
import useNextUpCardsContext from '#client/hooks/useNextUpCardsContext';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { DEFAULT_SEGMENT } from '#client/manifest';
import { dispatchRefetchCheckDeposit } from '#client/utils/event';
import { checkImageDataToRotate } from '#client/utils/images';

import CapturedImage from './CapturedImage';
import {
  DialogContentStyled,
  DividerStyled,
  SectionWrapperStyled,
  Wrapper
} from './CheckDepositOverview.styles';
import DepositMoneyInput from './DepositMoneyInput';
import DepositTo from './DepositTo';

import { MODAL_ACTION } from '../constants/actions';
import { CAPTURE_BACK_SIDE, CAPTURE_FRONT_SIDE } from '../constants/captureSide';
import { CHECK_DEPOSIT_FAILED_ERROR_MESSAGE } from '../constants/messages';
import { CHECK_DEPOSIT_UNIT_STATUS } from '../constants/status';
import { convertBase64File } from '../utils/imageHandler';

const CheckDepositOverview = ({ handleClose }) => {
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const { accessToken } = useAuthentication();
  const { getCustomerToken } = useCustomerToken({ type: TOKEN_TYPES.COMMON });
  const captureException = useCaptureException();
  const { remains, error: errorLimit, limit } = useCheckDepositLimits();
  const { dailyCheckDepositRemain, monthlyCheckDepositRemain } = remains || {};
  const { daily, monthly } = limit || {};
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId } = useUnitAccountId();
  const { createCommonErrorToast, createErrorToast } = useToastContext();
  const { openModal } = useModalContext();
  const { isOpeningModal } = useCheckOpenModal();
  const {
    isLoadingFrontImage,
    isLoadingBackImage,
    warningFrontImage,
    warningBackImage,
    setIsAutoCaptureMode,
    checkCameraPermission
  } = useMitekSdkContext();
  const { refetchForFirstCheckDeposit } = useCheckTransactionContext();
  const { removeCardWithAnimation } = useNextUpCardsContext();
  const { depositData, dispatchDepositData } = useFormStep();

  const [moneyInput, setMoneyInput] = useState({
    value: 0,
    isValid: false,
    isError: false,
    message: ''
  });
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDisableSubmit = Boolean(
    !moneyInput.isValid ||
      !frontImage ||
      !backImage ||
      isSubmitting ||
      warningFrontImage ||
      warningBackImage
  );

  const createErrorToastHandler = (title, message, error) => {
    handleClose();
    if (!title || !message) {
      createCommonErrorToast({ error });
      return;
    }
    const showReviewCTA = title !== CHECK_DEPOSIT_FAILED_ERROR_MESSAGE;
    createErrorToast({
      title,
      description: message,
      secondaryAction: showReviewCTA
        ? {
            label: 'Review',
            size: 'sm',
            onClick: () => {
              openModal({
                name: MODAL_NAMES.CHECK_DEPOSIT_HISTORY,
                component: CheckDepositHistory
              });
            }
          }
        : {},
      error
    });
  };

  const submitCheckDepositHandler = async () => {
    setIsSubmitting(true);
    const customerToken = await getCustomerToken();

    const shouldStopSubmit = !customerToken || !accessToken || !unitAccountId;
    if (shouldStopSubmit) {
      setIsSubmitting(false);
      return;
    }

    // init FormData instant
    const form = new FormData();

    // convert base64 to image
    const [front, back] = await Promise.all([
      convertBase64File(frontImage, 'frontImage'),
      convertBase64File(backImage, 'backIamge')
    ]);

    form.append('front', front);
    form.append('back', back);
    form.append('amount', moneyInput.value);

    const path = generatePath(
      `${DEFAULT_SEGMENT}/check-deposits/:businessEntityUuid/:unitAccountId/submit`,
      {
        businessEntityUuid,
        unitAccountId
      }
    );
    try {
      const { data, status: statusCode } = await axios.post(path, form, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data',
          'Customer-Token': customerToken,
          'Idempotency-Key': depositData.idempotencyKey
        }
      });

      // submit success
      if (data && statusCode === 200) {
        const {
          attributes: { status, reason },
          id
        } = data;

        // handler for Rejected or AwaitingCustomerConfirmation
        if (
          [
            CHECK_DEPOSIT_UNIT_STATUS.REJECTED,
            CHECK_DEPOSIT_UNIT_STATUS.AWAITING_CUSTOMER_CONFIRMATION
          ].includes(status)
        ) {
          // open loading animation and pass the payload data to reducer
          dispatchDepositData({
            type: MODAL_ACTION.START_LOADING,
            payload: {
              status,
              reason,
              depositId: id
            }
          });
        } else {
          // dispatch action to store some data from API to the reducer
          const payload = {
            depositId: id,
            status,
            reason,
            title: '', // hide title when show animation
            loading: true,
            amount: moneyInput.value
          };

          // with happy path first
          dispatchDepositData({
            type: MODAL_ACTION.GO_SUBMISSION_OVERVIEW,
            payload
          });

          refetchForFirstCheckDeposit();
          // remove card from next up cards
          removeCardWithAnimation(FUND_BANK_ACCOUNT);

          if (!isOpeningModal) {
            dispatchRefetchCheckDeposit();
          }
        }
      } else {
        throw new Error(`Submission check deposit failed with statusCode: ${statusCode}`);
      }
    } catch (error) {
      const title = error?.response?.data?.title || '';
      const message = error?.response?.data?.message || '';

      captureException(error);
      createErrorToastHandler(title, message, error);
    } finally {
      setIsSubmitting(false);
      setIsAutoCaptureMode(true);
    }
  };

  const saveCapturedImageHandler = useCallback(
    async (imageData, docType) => {
      if (!imageData || !docType) {
        return;
      }
      try {
        const rotatedImage = await checkImageDataToRotate(imageData);
        if (docType === CHECK_FRONT_DOC_TYPE) {
          setFrontImage(rotatedImage);
        } else {
          setBackImage(rotatedImage);
        }
      } catch (error) {
        captureException(error);
      }
    },
    [captureException]
  );

  useEffect(() => {
    if (errorLimit) {
      handleClose();
      createCommonErrorToast({ error: errorLimit });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorLimit]);

  useEffect(() => {
    checkCameraPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack
      height="100%"
      mx={0}
    >
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper>
              <Stack
                gap="16px"
                padding="32px 24px"
              >
                <DepositMoneyInput
                  constants={{
                    dailyLimit: daily,
                    monthlyLimit: monthly
                  }}
                  defaultValue={0}
                  handleDataChange={setMoneyInput}
                  isDisable={isSubmitting}
                  remainLimits={{
                    dailyRemainLimit: dailyCheckDepositRemain,
                    monthlyRemainLimit: monthlyCheckDepositRemain
                  }}
                />
                <ViewAllTransactionLimitButton />
              </Stack>
              <SectionWrapperStyled>
                <DepositTo title="Deposit to" />
                <DividerStyled />
                <CapturedImage
                  imageData={frontImage}
                  isDisable={isSubmitting || isLoadingBackImage}
                  loading={isLoadingFrontImage}
                  saveCapturedImageHandler={saveCapturedImageHandler}
                  sideType={CAPTURE_FRONT_SIDE}
                  warningMessage={warningFrontImage}
                />
                <DividerStyled />
                <CapturedImage
                  imageData={backImage}
                  isDisable={isSubmitting || isLoadingFrontImage}
                  loading={isLoadingBackImage}
                  saveCapturedImageHandler={saveCapturedImageHandler}
                  sideType={CAPTURE_BACK_SIDE}
                  warningMessage={warningBackImage}
                />
                <DividerStyled />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                >
                  <Text
                    color="primary"
                    size={isDesktop ? 'lg' : 'md'}
                  >
                    Auto capture
                  </Text>
                  <Switch
                    checked
                    textColor="primary"
                  />
                </Stack>
              </SectionWrapperStyled>
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionButtonsGrid>
        <LoadingButton
          aria-label="submit"
          color="primary"
          disabled={isDisableSubmit || isLoadingBackImage || isLoadingFrontImage}
          fullWidth
          loading={isSubmitting}
          onClick={submitCheckDepositHandler}
          variant="contained"
        >
          Submit
        </LoadingButton>
      </ActionButtonsGrid>
    </Stack>
  );
};

CheckDepositOverview.propTypes = {
  handleClose: PropTypes.func.isRequired
};

export default memo(CheckDepositOverview);
